import React, { useMemo, useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Box, Typography, Stack, Divider } from '@mui/material';
import useTrackPageView from '../../hooks/useTrackPageView';
import { PAGE_TYPE, CONTENT_TYPE } from '../../constants/gaEnums';
import { useAlbumDetail, useAlbumDetailBySlug } from './hooks/useAlbumDetail';
import { useAlbumReviews } from './hooks/useAlbumReviews';
import PageLayout from '../../components/layout/PageLayout';
import ProgressBar from '../../components/common/ProgressBar';
import AlbumCover from '../../components/common/AlbumCover';
import AlbumTitleLink from '../../components/common/AlbumTitleLink';
import ArtistLink from '../../components/common/ArtistLink';
import RatingReadOnly from '../../components/common/RatingReadOnly';
import ReviewCount from '../../components/common/ReviewCount';
import BookmarkCount from '../../components/common/BookmarkCount';
import SelectFilter from '../../components/common/SelectFilter';
import MyReviewSection from './components/my-review/MyReviewSection';
import UserReviewItem from './components/user-review/UserReviewItem';
import InFeedBoxAd from '../../components/ad/InFeedBoxAd';
import SidebarLayout from '../../components/layout/SidebarLayout';
import Pagination from '../../components/common/Pagination';
import theme from '../../theme';

const ITEMS_PER_PAGE = 10;

const REVIEW_SORT_OPTIONS = [
  { label: '좋아요순', value: 'likes' },
  { label: '최신순', value: 'latest' },
  { label: '높은 평점순', value: 'rating_high' },
  { label: '낮은 평점순', value: 'rating_low' },
];

const sortReviewsBy = (reviews, sortType) => {
  const sorted = [...reviews];
  switch (sortType) {
    case 'likes':
      return sorted.sort((a, b) => b.likeCount - a.likeCount || b.id - a.id);
    case 'latest':
      return sorted.sort((a, b) => new Date(b.createdAt) - new Date(a.createdAt) || b.id - a.id);
    case 'rating_high':
      return sorted.sort((a, b) => b.rating - a.rating || b.id - a.id);
    case 'rating_low':
      return sorted.sort((a, b) => a.rating - b.rating || b.id - a.id);
    default:
      return sorted;
  }
};

const UserReviews = () => {
  const params = useParams();
  const albumSlug = params.albumSlug;
  const paramSource = params.albumSource;
  const paramId = params.sourceId;
  const navigate = useNavigate();
  const [currentPage, setPage] = useState(1);
  const [sortBy, setSortBy] = useState('likes');
  const [reviewIds, setReviewIds] = useState([]);

  const slugResult = useAlbumDetailBySlug(albumSlug);
  const { data: directAlbumInfo, isLoading: directLoading } = useAlbumDetail(paramSource, paramId);

  const albumSource = albumSlug ? slugResult.albumSource : paramSource;
  const sourceId = albumSlug ? slugResult.sourceId : paramId;
  const albumInfo = albumSlug ? slugResult.data : directAlbumInfo;
  const albumLoading = albumSlug ? slugResult.isLoading : directLoading;

  const albumUrl = albumSlug ? `/reviews/${albumSlug}` : `/reviews/${albumSource}/${sourceId}`;

  useTrackPageView(
    {
      pageType: PAGE_TYPE.REVIEWS_USERREVIEWS,
      contentType: CONTENT_TYPE.ALBUM,
      contentId: sourceId,
      contentName: albumInfo?.title,
    },
    { ready: !!sourceId },
  );

  const { data: allReviews = [] } = useAlbumReviews(albumSource, sourceId);

  // 초기 로드 시 정렬 + 리뷰 추가/삭제 시 반영
  useEffect(() => {
    if (allReviews.length > 0) {
      setReviewIds((prev) => {
        if (prev.length === 0) {
          const sorted = sortReviewsBy(allReviews, sortBy);
          return sorted.map((r) => r.id);
        }
        const allIdSet = new Set(allReviews.map((r) => r.id));
        const prevSet = new Set(prev);
        const hasNew = allReviews.some((r) => !prevSet.has(r.id));
        const hasDeleted = prev.some((id) => !allIdSet.has(id));
        if (hasNew || hasDeleted) {
          return sortReviewsBy(allReviews, sortBy).map((r) => r.id);
        }
        return prev;
      });
    }
  }, [allReviews, sortBy]);

  // ID 순서대로 allReviews에서 찾아서 최신 데이터 사용
  const sortedReviews = useMemo(() => {
    return reviewIds.map((id) => allReviews.find((r) => r.id === id)).filter(Boolean);
  }, [reviewIds, allReviews]);

  const pagination = useMemo(() => {
    const totalItems = sortedReviews.length;
    const totalPages = Math.ceil(totalItems / ITEMS_PER_PAGE);
    return { currentPage, totalPages, totalItems, itemsPerPage: ITEMS_PER_PAGE };
  }, [sortedReviews.length, currentPage]);

  const reviews = useMemo(() => {
    const startIndex = (currentPage - 1) * ITEMS_PER_PAGE;
    const endIndex = startIndex + ITEMS_PER_PAGE;
    return sortedReviews.slice(startIndex, endIndex);
  }, [sortedReviews, currentPage]);

  const handleSortChange = (newSortBy) => {
    setSortBy(newSortBy);
    const sorted = sortReviewsBy(allReviews, newSortBy);
    setReviewIds(sorted.map((r) => r.id));
    setPage(1);
  };

  return (
    <PageLayout maxWidth={'980px'}>
      <ProgressBar isLoading={albumLoading}>
        <SidebarLayout pageType={PAGE_TYPE.REVIEWS_USERREVIEWS}>
          {albumInfo && albumInfo.artist && (
            <>
              {/* 앨범 정보 헤더 */}
              <Box sx={{ display: 'flex', gap: { xs: '10px', md: '16px', lg: '20px' } }}>
                <Box
                  sx={{
                    width: { xs: '30%', md: '23.5%', lg: 180 },
                    minWidth: { xs: 96, md: 168 },
                    maxWidth: { lg: 180 },
                    flexShrink: 0,
                  }}
                >
                  <AlbumCover
                    imageUrl={albumInfo.imageUrl}
                    altText={albumInfo.title}
                    isStarred={albumInfo.isStarred}
                    onClick={() => navigate(albumUrl)}
                    sx={{ width: '100%', aspectRatio: '1 / 1' }}
                  />
                </Box>
                <Box
                  sx={{
                    flex: 1,
                    minWidth: 0,
                    display: 'flex',
                    flexDirection: 'column',
                  }}
                >
                  {/* 앨범명 */}
                  <AlbumTitleLink
                    title={albumInfo.title}
                    to={albumUrl}
                    contentRating={albumInfo.contentRating}
                    lineClamp={'unset'}
                    sx={{
                      fontSize: { xs: 14, md: 18 },
                      fontWeight: 700,
                      mb: { xs: '8px', md: '12px', lg: '16px' },
                    }}
                    iconSx={{
                      width: { xs: '12px', md: '14px' },
                      height: { xs: '12px', md: '14px' },
                      verticalAlign: '-0.06em',
                    }}
                  />
                  {/* 가수 */}
                  <ArtistLink
                    artistName={albumInfo.artist.name}
                    artistSource={albumInfo.artist.artistSource}
                    artistSourceId={albumInfo.artist.sourceId}
                    artistSlug={albumInfo.artist.slug}
                    artistDbId={albumInfo.artist.id}
                    sx={{
                      fontSize: { xs: 13, md: 16 },
                      mb: { xs: '12px', md: '12px', lg: '16px' },
                    }}
                  />
                  {/* 별점/리뷰/저장 (lg/md) */}
                  <Stack
                    direction="row"
                    spacing={1}
                    alignItems="center"
                    mb={{ md: '12px', lg: '16px' }}
                    sx={{ display: { xs: 'none', md: 'flex' } }}
                  >
                    <RatingReadOnly value={albumInfo.averageRating} variant={'fullResponsive'} />
                    <Divider
                      orientation="vertical"
                      flexItem
                      sx={{
                        height: '8px',
                        alignSelf: 'center',
                        backgroundColor: theme.palette.grey.line,
                      }}
                    />
                    <ReviewCount variant="detail" reviewCount={albumInfo.reviewCount} />
                    <BookmarkCount variant="detail" bookmarkCount={albumInfo.bookmarkCount} />
                  </Stack>
                  {/* 별점만 (xs) */}
                  <Box mb={'8px'} sx={{ display: { xs: 'block', md: 'none' } }}>
                    <RatingReadOnly variant={'fullResponsive'} value={albumInfo.averageRating} />
                  </Box>
                  {/* 발매일 (lg/md) */}
                  <Typography
                    color={theme.palette.black[4]}
                    fontSize={14}
                    sx={{ display: { xs: 'none', md: 'block' }, whiteSpace: 'nowrap' }}
                  >
                    {albumInfo.releaseDate}
                  </Typography>
                  {/* 발매일 | 리뷰/저장 (xs) */}
                  <Stack
                    direction="row"
                    spacing={1}
                    alignItems="center"
                    sx={{ display: { xs: 'flex', md: 'none' } }}
                  >
                    <Typography
                      color={theme.palette.black[4]}
                      fontSize={12}
                      sx={{ whiteSpace: 'nowrap' }}
                    >
                      {albumInfo.releaseDate}
                    </Typography>
                    <Divider
                      orientation="vertical"
                      flexItem
                      sx={{
                        height: '8px',
                        alignSelf: 'center',
                        backgroundColor: theme.palette.grey.line,
                      }}
                    />
                    <ReviewCount variant="card" reviewCount={albumInfo.reviewCount} />
                    <BookmarkCount variant="card" bookmarkCount={albumInfo.bookmarkCount} />
                  </Stack>
                </Box>
              </Box>

              {/* My Review */}
              <Box mt={{ xs: '30px', md: '40px' }}>
                <MyReviewSection albumSource={albumSource} sourceId={sourceId} />
              </Box>

              {/* 광고 */}
              <Box mt={{ xs: '30px', md: '40px', lg: '30px' }}>
                <InFeedBoxAd pageType={PAGE_TYPE.REVIEWS_USERREVIEWS} />
              </Box>

              {/* User Reviews 리스트 */}
              <Box mt={{ xs: '30px', md: '40px', lg: '30px' }}>
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
                  <Box
                    sx={{
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'space-between',
                      pb: 1,
                      borderBottom: `1px solid ${theme.palette.grey.line}`,
                    }}
                  >
                    <Typography
                      fontSize={{ xs: 16, md: 18 }}
                      fontWeight={'bold'}
                      color={theme.palette.black[9]}
                    >
                      회원 리뷰 ({pagination.totalItems})
                    </Typography>
                    <SelectFilter
                      label="정렬"
                      value={sortBy}
                      onChange={handleSortChange}
                      options={REVIEW_SORT_OPTIONS}
                    />
                  </Box>
                  <Box sx={{ width: '100%' }}>
                    {reviews.map((review) => (
                      <UserReviewItem
                        key={review.id}
                        review={review}
                        albumSource={albumSource}
                        sourceId={sourceId}
                      />
                    ))}
                  </Box>
                </Box>
                <Pagination
                  count={pagination.totalPages}
                  page={pagination.currentPage}
                  onChange={(_, page) => setPage(page)}
                  marginTop={{ lg: '30px', md: '40px', xs: '18px' }}
                />
              </Box>
            </>
          )}
        </SidebarLayout>
      </ProgressBar>
    </PageLayout>
  );
};

export default UserReviews;
